/**
 * Aura Valen — adaptive colour for the transparent header (logo and icons) over photography.
 *
 * The transparent header sits on top of the first image (homepage hero, product gallery). A fixed white or
 * black treatment fails on some photos: a centred logo can fall on a dark garment while the icons sit on a
 * pale studio background. This script samples the pixels of the image directly behind the logo and behind
 * the icons and picks, for each, the tone (white or near-black) with the higher contrast. It never darkens
 * or alters the photo. Images are same-origin (/cdn/shop), so the already-loaded <img> is read; no extra
 * requests are made. Without JavaScript, or when no image is found, the template default applies.
 */
(() => {
  const header = document.querySelector('header-component[transparent]');
  if (!header) return;

  const WHITE = [255, 255, 255];
  const DARK = [17, 17, 17];
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;

  const lin = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luminance = (r, g, b) => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  const contrast = (l1, l2) => (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
  const L_WHITE = luminance(...WHITE);
  const L_DARK = luminance(...DARK);

  /** @param {HTMLImageElement} img @param {DOMRect} box */
  const sample = (img, box) => {
    if (!img.complete || !img.naturalWidth) return null;
    const rect = img.getBoundingClientRect();
    const style = getComputedStyle(img);
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    let sx = rect.width / nw;
    let sy = rect.height / nh;
    if (style.objectFit === 'cover') sx = sy = Math.max(sx, sy);
    else if (style.objectFit === 'contain' || style.objectFit === 'scale-down') sx = sy = Math.min(sx, sy);
    else if (style.objectFit === 'none') sx = sy = 1;
    const [px, py] = style.objectPosition.split(' ').map((v) => (v.endsWith('%') ? parseFloat(v) / 100 : 0.5));
    const offX = rect.left + (rect.width - nw * sx) * (px ?? 0.5);
    const offY = rect.top + (rect.height - nh * sy) * (py ?? 0.5);
    // Visible part of the box that lies over the drawn image
    const x0 = Math.max(box.left, offX, rect.left);
    const y0 = Math.max(box.top, offY, rect.top);
    const x1 = Math.min(box.right, offX + nw * sx, rect.right);
    const y1 = Math.min(box.bottom, offY + nh * sy, rect.bottom);
    if (x1 - x0 < 2 || y1 - y0 < 2) return null;
    // Draw the whole image into a small canvas, then read the box in normalised image coordinates.
    // (Avoids drawImage source-rectangle units, which differ for srcset images.)
    const drawnW = nw * sx;
    const drawnH = nh * sy;
    const cw = 160;
    const ch = Math.max(1, Math.round((cw * nh) / nw));
    canvas.width = cw;
    canvas.height = ch;
    try {
      ctx.drawImage(img, 0, 0, cw, ch);
      const rx = Math.floor(((x0 - offX) / drawnW) * cw);
      const ry = Math.floor(((y0 - offY) / drawnH) * ch);
      const rw = Math.max(1, Math.ceil(((x1 - x0) / drawnW) * cw));
      const rh = Math.max(1, Math.ceil(((y1 - y0) / drawnH) * ch));
      const data = ctx.getImageData(rx, ry, Math.min(rw, cw - rx), Math.min(rh, ch - ry)).data;
      const lums = [];
      for (let i = 0; i < data.length; i += 4) lums.push(luminance(data[i], data[i + 1], data[i + 2]));
      return lums;
    } catch {
      return null; // cross-origin or decode issue: keep the default
    }
  };

  /** Lower-quartile contrast of a tone against the sampled pixels (robust to small highlights). */
  const score = (lums, toneL) => {
    const values = lums.map((l) => contrast(l, toneL)).sort((a, b) => a - b);
    return values[Math.floor(values.length * 0.25)];
  };

  /** Image directly under a point, ignoring the header itself. */
  const imageAt = (x, y) => {
    for (const el of document.elementsFromPoint(x, y)) {
      if (header.contains(el)) continue;
      if (el instanceof HTMLImageElement) return el;
    }
    return null;
  };

  const pickTone = (boxes, key) => {
    const all = [];
    for (const box of boxes) {
      const img = imageAt(box.left + box.width / 2, box.top + box.height / 2);
      const lums = img && sample(img, box);
      if (lums) all.push(lums);
    }
    if (!all.length) return null;
    // Choose the tone whose worst element still has the best contrast
    const white = Math.min(...all.map((l) => score(l, L_WHITE)));
    const dark = Math.min(...all.map((l) => score(l, L_DARK)));
    header.dataset[key] = `${white.toFixed(2)}/${dark.toFixed(2)}`; // white/dark lower-quartile contrast (QA)
    return white > dark ? 'light' : 'dark';
  };

  const visibleBoxes = (selector) =>
    [...header.querySelectorAll(selector)]
      .filter((el) => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden')
      .map((el) => el.getBoundingClientRect())
      .filter((r) => r.width > 6 && r.height > 6 && r.bottom > 0);

  let frame = 0;
  const update = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      if (header.dataset.stickyState === 'active') return;
      const logoTone = pickTone(visibleBoxes('.header-logo__image'), 'avLogoScore');
      const iconTone = pickTone(visibleBoxes('.header__row--top .header-actions__action svg, .header__row--top header-drawer summary svg, .header__row--top .search-action svg'), 'avIconScore');
      if (logoTone) header.dataset.avLogoTone = logoTone;
      if (iconTone) {
        header.dataset.avIconTone = iconTone;
        const light = iconTone === 'light';
        header.style.setProperty('--color-transparent-text', light ? '#FFFFFF' : '#111111');
        header.style.setProperty('--color-transparent-text-rgb', light ? '255 255 255' : '17 17 17');
        header.style.setProperty('--color-transparent-text-contrast', light ? 'var(--palette-darkest)' : 'var(--palette-lightest)');
      }
    });
  };

  let settle;
  const updateSoon = () => {
    clearTimeout(settle);
    settle = setTimeout(update, 120);
  };

  // First image may still be loading
  const watchImages = () => {
    document.querySelectorAll('main img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', updateSoon, { once: true });
    });
  };

  watchImages();
  update();
  // Any image that finishes loading (variant swap, lazy slide) may change what sits under the header
  document.addEventListener('load', (event) => { if (event.target instanceof HTMLImageElement) updateSoon(); }, true);
  window.addEventListener('load', updateSoon);
  window.addEventListener('resize', updateSoon, { passive: true });
  // Gallery swipes (scroll inside the slideshow) and variant changes that swap the gallery images
  document.addEventListener('scroll', updateSoon, { capture: true, passive: true });
  const gallery = document.querySelector('media-gallery, .av-hero');
  if (gallery) {
    new MutationObserver(() => {
      watchImages();
      updateSoon();
      setTimeout(update, 700);
    }).observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'aria-hidden', 'hidden'] });
  }
})();
